import { useState } from "react";
import { useSaveVorlage, useVorlagen } from "../../hooks/useStammdaten";
import { errorText, euro, type WebInvoiceDraft, type WebInvoiceLine } from "../../lib/invoices";
import { newInvoiceLine } from "../../lib/invoiceEditor";
import type { BelegVorlage } from "../../lib/types";

type Props = {
  draft: WebInvoiceDraft;
  onChange: (draft: WebInvoiceDraft) => void;
  /** Im Reiter von InvoiceWerkzeuge: ohne eigene Karte und Überschrift. */
  eingebettet?: boolean;
};

/**
 * Beleg-Vorlagen — wiederkehrende Leistungsstrukturen.
 *
 * Eine Vorlage enthält Positionen, Blöcke und die beiden Texte. Kunde,
 * Belegnummer, Daten, Rabatt und Projektbezug bleiben bewusst draußen: das
 * sind Eigenschaften des einzelnen Belegs, nicht der Leistung. Beim Anwenden
 * werden die Positionen deshalb **angehängt** und nicht ersetzt — sonst wäre
 * eine versehentlich angewandte Vorlage nicht mehr rückgängig zu machen.
 */
export default function InvoiceVorlagen({ draft, onChange, eingebettet = false }: Props) {
  const { data, isLoading } = useVorlagen();
  const sichern = useSaveVorlage();
  const [name, setName] = useState("");
  const [beschreibung, setBeschreibung] = useState("");
  const [offen, setOffen] = useState(false);
  const [hinweis, setHinweis] = useState("");
  const vorlagen = data?.items || [];

  const anwenden = (vorlage: BelegVorlage) => {
    const neueZeilen: WebInvoiceLine[] = (vorlage.line_rows || []).map((zeile) => ({
      ...newInvoiceLine(),
      line_kind: (zeile.line_kind as WebInvoiceLine["line_kind"]) || "position",
      title: String(zeile.title || ""),
      description: String(zeile.description || ""),
      quantity: Number(zeile.quantity) || 0,
      unit_label: String(zeile.unit_label || "pauschal"),
      unit_code: String(zeile.unit_code || "C62"),
      unit_price_net: Number(zeile.unit_price_net) || 0,
      vat_rate_percent: ([0, 7, 19].includes(Number(zeile.vat_rate_percent))
        ? Number(zeile.vat_rate_percent)
        : 19) as 0 | 7 | 19,
    }));
    if (!neueZeilen.length) { setHinweis("Diese Vorlage enthält keine Positionen."); return; }
    // Eine unberührte Startzeile ist Platzhalter, kein Inhalt — die darf weg.
    const bestehende = draft.line_rows.filter(
      (z) => z.title.trim() || z.description.trim() || z.unit_price_net !== 0,
    );
    onChange({
      ...draft,
      line_rows: [...bestehende, ...neueZeilen],
      intro_paragraphs: draft.intro_paragraphs.trim() || vorlage.intro_paragraphs,
      notes_after_table: draft.notes_after_table.trim() || vorlage.notes_after_table,
    });
    setHinweis(`„${vorlage.name}" angehängt: ${neueZeilen.length} Zeilen.`);
  };

  const speichern = () => {
    sichern.mutate({
      name: name.trim(),
      beschreibung: beschreibung.trim(),
      kind: draft.kind,
      intro_paragraphs: draft.intro_paragraphs,
      notes_after_table: draft.notes_after_table,
      line_rows: draft.line_rows,
    }, {
      onSuccess: (vorlage) => {
        setHinweis(`Als Vorlage „${vorlage.name}" gesichert.`);
        setName(""); setBeschreibung(""); setOffen(false);
      },
    });
  };

  const Huelle = eingebettet ? "div" : "section";
  return (
    <Huelle className={eingebettet ? "invoice-werkzeug-inhalt" : "invoice-editor-card"}>
      <div className="invoice-editor-card-head">
        {eingebettet ? (
          <p className="invoice-editor-hint">Positionen einer Vorlage werden angehängt, nichts wird ersetzt.</p>
        ) : (
          <div>
            <p className="invoice-kicker">Wiederverwenden</p>
            <h2>Vorlagen</h2>
          </div>
        )}
        <button className="invoice-button" type="button" onClick={() => setOffen((v) => !v)}>
          {offen ? "Abbrechen" : "Als Vorlage sichern"}
        </button>
      </div>

      {offen && (
        <div className="invoice-vorlage-form">
          <label className="invoice-editor-field">
            <span>Name *</span>
            <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="z. B. Heckenschnitt Standard" />
          </label>
          <label className="invoice-editor-field">
            <span>Beschreibung</span>
            <input value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} placeholder="optional" />
          </label>
          <button
            className="invoice-button primary"
            type="button"
            disabled={!name.trim() || sichern.isPending}
            onClick={speichern}
          >
            {sichern.isPending ? "Sichert …" : "Sichern"}
          </button>
          <p className="invoice-editor-hint">
            Gesichert werden Positionen, Blöcke und die beiden Texte — Kunde, Nummer und
            Daten nicht. Ein vorhandener Name wird überschrieben.
          </p>
        </div>
      )}

      {hinweis && <p className="invoice-inline-note" role="status">{hinweis}</p>}
      {sichern.isError && <p className="invoice-inline-error">{errorText(sichern.error)}</p>}

      {isLoading && <p className="invoice-editor-hint">Vorlagen werden geladen …</p>}
      {!isLoading && vorlagen.length === 0 && (
        <p className="invoice-editor-hint">Noch keine Vorlage gesichert.</p>
      )}
      {vorlagen.length > 0 && (
        <div className="invoice-vorlage-liste">
          {vorlagen.map((vorlage) => (
            <button type="button" key={vorlage.id} onClick={() => anwenden(vorlage)}>
              <span>
                <strong>{vorlage.name}</strong>
                <small>{vorlage.positionen} Positionen{vorlage.beschreibung ? ` · ${vorlage.beschreibung}` : ""}</small>
              </span>
              <span className="invoice-vorlage-summe">{euro(vorlage.summe_netto)}<small>anhängen ›</small></span>
            </button>
          ))}
        </div>
      )}
    </Huelle>
  );
}
